import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../utils/api';
import { Badge } from './ui/badge';
import { Tag, RefreshCw } from 'lucide-react';

/**
 * Coupons section shown inside PatientProfileModal.
 * Lists all Google-Review coupons issued to the patient with their status.
 * Coupons are auto-issued by the WhatsApp inbound review-verifier flow and
 * consumed by the Teller at checkout (as a payment-method component).
 */
const statusColor = {
  active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  used: 'bg-slate-100 text-slate-600 border-slate-200',
  expired: 'bg-red-100 text-red-700 border-red-200',
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch (_e) {
    return iso;
  }
};

const PatientCouponsSection = ({ patientId }) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const load = useCallback(async () => {
    if (!patientId) return;
    try {
      setLoading(true);
      const res = await api.get(`/coupons/patient/${patientId}`);
      setRows(res.data?.rows || []);
    } catch (_e) {
      toast.error('Failed to load coupons');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    load();
  }, [load]);

  const activeCount = rows.filter((r) => r.status === 'active').length;

  return (
    <div data-testid="patient-coupons-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <Tag className="w-5 h-5 text-amber-600" />
          Discount Coupons
          <Badge variant="outline" className="text-xs" data-testid="patient-coupons-count">
            {rows.length}
          </Badge>
          {activeCount > 0 && (
            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">
              {activeCount} active
            </Badge>
          )}
        </h3>
        <button
          onClick={load}
          className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
          data-testid="patient-coupons-refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {loading && rows.length === 0 && (
        <div className="text-sm text-gray-500 py-3">Loading coupons...</div>
      )}

      {!loading && rows.length === 0 && (
        <div className="text-sm text-gray-500 bg-gray-50 rounded-xl p-4 text-center border border-dashed border-gray-200">
          No coupons yet. Coupons are issued after Google-review verification.
        </div>
      )}

      {rows.length > 0 && (
        <div className="space-y-2">
          {rows.map((c) => (
            <div
              key={c.id}
              className={`flex items-center gap-3 p-3 rounded-xl border ${
                c.status === 'active'
                  ? 'bg-amber-50 border-amber-200'
                  : 'bg-white border-gray-200'
              }`}
              data-testid={`patient-coupon-${c.code}`}
            >
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Tag className="w-5 h-5 text-amber-700" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono font-bold text-gray-900">{c.code}</span>
                  <Badge className={`text-xs ${statusColor[c.status] || statusColor.expired}`}>
                    {c.status}
                  </Badge>
                  <span className="text-sm font-semibold text-emerald-700">
                    ₹{Number(c.discount_amount || 0)}
                  </span>
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  Issued {fmtDate(c.issued_at)} • Expires {fmtDate(c.expires_at)}
                  {c.source && ` • ${c.source}`}
                </div>
                {c.status === 'used' && c.used_at && (
                  <div className="text-xs text-gray-500 mt-0.5">
                    Used {fmtDate(c.used_at)}
                    {c.used_by_user_name && ` by ${c.used_by_user_name}`}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PatientCouponsSection;
